import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { useTranslation } from '../../contexts/TranslationContext';
import DropdownNavigation from '../../components/ui/dropdown-navigation';
import Footer from '../../components/ui/footer';
import CookieConsent from '../../components/ui/CookieConsent';
import { Menu, X, ChevronRight, Home, Briefcase, Users, Wrench, MessageSquare, Globe, Sparkles, Rocket, Brain, Building2, TrendingUp, Zap, Globe as WebIcon, BarChart3, Bot, Users as CommunityIcon, Smartphone, Zap as AutomationIcon, Search, ClipboardList, Settings, Building, Phone, Plane, Train, ShoppingCart, GraduationCap, ArrowRight, Loader2 } from 'lucide-react';

const StatsAI = () => {
  const { t, language, changeLanguage, isLoading } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [isGetStartedLoading, setIsGetStartedLoading] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false);

  // Debug: Log when language changes
  useEffect(() => {
    console.log('Language changed to:', language);
    console.log('RTL class should be:', language === 'ar' ? 'rtl' : 'ltr');
  }, [language]);

  // Handle Get Started button click
  const handleGetStarted = async () => {
    setIsGetStartedLoading(true);
    setButtonClicked(true);
    
    // Simulate loading time for better UX
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Navigate to contact page
    window.location.href = '/contact';
  };


  // Load Spline 3D script
  useEffect(() => {
    if (!document.querySelector('script[src*="spline-viewer"]')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://unpkg.com/@splinetool/viewer@1.10.57/build/spline-viewer.js';
      (document.head || document.body).appendChild(script);
    }
  }, []);

  // Show loading state while translations are loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Get testimonials from translations
  const testimonialsData = t('testimonials.items');
  const testimonials = Array.isArray(testimonialsData) ? testimonialsData : [];

  // Navigation data for mobile menu
  const navigationData = [
    {
      title: t('nav.home'),
      href: '/',
      icon: Home,
      submenu: [
        { title: t('nav.overview'), href: '/', icon: Home },
        { title: t('nav.aboutUs'), href: '/about', icon: Building2 },
        { title: t('nav.vision2030'), href: '/vision-2030', icon: Globe }
      ]
    },
    {
      title: t('nav.services'),
      href: '/services',
      icon: Briefcase,
      submenu: [
        { title: t('nav.allServices'), href: '/services', icon: Briefcase },
        { title: t('nav.webDevelopment'), href: '/services', icon: Rocket },
        { title: t('nav.aiSolutions'), href: '/services', icon: Brain },
        { title: t('nav.analytics'), href: '/services', icon: TrendingUp }
      ]
    },
    {
      title: t('nav.solutions'),
      href: '/solutions',
      icon: Sparkles,
      submenu: [
        { title: t('nav.allSolutions'), href: '/solutions', icon: Sparkles },
        { title: t('nav.businessSolutions'), href: '/solutions', icon: Building2 },
        { title: t('nav.techSolutions'), href: '/solutions', icon: Zap }
      ]
    },
    {
      title: t('nav.resources'),
      href: '/blog',
      icon: MessageSquare,
      submenu: [
        { title: t('nav.blog'), href: '/blog', icon: MessageSquare },
        { title: t('nav.caseStudies'), href: '/case-studies', icon: Users },
        { title: t('nav.testimonials'), href: '/testimonials', icon: Users }
      ]
    },
    {
      title: t('nav.technology'),
      href: '/technology',
      icon: Wrench
    },
    {
      title: t('nav.contact'),
      href: '/contact',
      icon: MessageSquare
    }
  ];

  const features = [
    {
      title: t('features.aiInsights.title'),
      description: t('features.aiInsights.description'),
      icon: Brain
    },
    {
      title: t('features.realTime.title'),
      description: t('features.realTime.description'),
      icon: Zap
    },
    {
      title: t('features.segmentation.title'),
      description: t('features.segmentation.description'),
      icon: TrendingUp
    },
    {
      title: t('features.reports.title'),
      description: t('features.reports.description'),
      icon: BarChart3
    },
    {
      title: t('features.conversion.title'),
      description: t('features.conversion.description'),
      icon: Sparkles
    },
    {
      title: t('features.predictive.title'),
      description: t('features.predictive.description'),
      icon: Globe
    }
  ];

  return (
    <div className={`min-h-screen bg-background ${language === 'ar' ? 'rtl' : 'ltr'}`} style={{ fontFamily: language === 'ar' ? "'Cairo', Arial, sans-serif" : "inherit" }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between h-16 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Logo - Hidden on mobile */}
            <div className="hidden md:flex items-center">
              <img
                src="/Logos/Asset 1front.svg"
                alt="Envael Logo" 
                className="w-8 h-8"
              />
            </div>

            {/* Navigation Links - Hidden on mobile */}
            <div className={`hidden md:flex items-center ${language === 'ar' ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
              <a href="/" className="text-muted-foreground hover:text-foreground transition-colors">{t('nav.home')}</a>
              <DropdownNavigation />
            </div>

            {/* CTA Buttons - Hidden on mobile */}
            <div className={`hidden md:flex items-center space-x-4 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              {/* Language Dropdown */}
              <div className="relative">
                <button 
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  onClick={() => {
                    const dropdown = document.getElementById('language-dropdown');
                    dropdown.classList.toggle('hidden');
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                  <span>{language.toUpperCase()}</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div 
                  id="language-dropdown"
                  className={`hidden absolute top-full mt-2 w-48 bg-background border border-border rounded-lg shadow-lg z-50 ${language === 'ar' ? 'left-0' : 'right-0'}`}
                >
                  <div className="py-2">
                    <button 
                      className={`flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors ${language === 'ar' ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent'}`}
                      onClick={() => {
                        changeLanguage('ar');
                        document.getElementById('language-dropdown').classList.add('hidden');
                      }}
                    >
                      <span className="text-lg">🇸🇦</span>
                      <span>العربية</span>
                      <span className="text-xs text-muted-foreground ml-auto">AR</span>
                    </button>
                    <button 
                      className={`flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors ${language === 'fr' ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent'}`}
                      onClick={() => {
                        changeLanguage('fr');
                        document.getElementById('language-dropdown').classList.add('hidden');
                      }}
                    >
                      <span className="text-lg">🇫🇷</span>
                      <span>Français</span>
                      <span className="text-xs text-muted-foreground ml-auto">FR</span>
                    </button>
                    <button 
                      className={`flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors ${language === 'en' ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent'}`}
                      onClick={() => {
                        changeLanguage('en');
                        document.getElementById('language-dropdown').classList.add('hidden');
                      }}
                    >
                      <span className="text-lg">🇺🇸</span>
                      <span>English</span>
                      <span className="text-xs text-muted-foreground ml-auto">EN</span>
                    </button>
                    <button 
                      className={`flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors ${language === 'es' ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent'}`}
                      onClick={() => {
                        changeLanguage('es');
                        document.getElementById('language-dropdown').classList.add('hidden');
                      }}
                    >
                      <span className="text-lg">🇪🇸</span>
                      <span>Español</span>
                      <span className="text-xs text-muted-foreground ml-auto">ES</span>
                    </button>
                    <button 
                      className={`flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors ${language === 'it' ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent'}`}
                      onClick={() => {
                        changeLanguage('it');
                        document.getElementById('language-dropdown').classList.add('hidden');
                      }}
                    >
                      <span className="text-lg">🇮🇹</span>
                      <span>Italiano</span>
                      <span className="text-xs text-muted-foreground ml-auto">IT</span>
                    </button>
                    <button 
                      className={`flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors ${language === 'de' ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent'}`}
                      onClick={() => {
                        changeLanguage('de');
                        document.getElementById('language-dropdown').classList.add('hidden');
                      }}
                    >
                      <span className="text-lg">🇩🇪</span>
                      <span>Deutsch</span>
                      <span className="text-xs text-muted-foreground ml-auto">DE</span>
                    </button>
                  </div>
                </div>
              </div>
              <a href="/contact">
                <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
                  {t('nav.contact')}
                </Button>
              </a>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                {t('nav.joinUp')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Full-Screen Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-background/90 backdrop-blur-md animate-in fade-in-0 duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu Content */}
          <div className="relative h-full flex flex-col animate-in slide-in-from-left-4 duration-300 ease-out">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center space-x-3">
                <img
                  src="/Logos/Asset 1front.svg"
                  alt="Envael Logo" 
                  className="w-8 h-8"
                />
                <span className="text-lg font-bold text-foreground">Envael</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Navigation Items */}
            <div className="flex-1 overflow-y-auto py-6">
              <div className="px-4 space-y-2">
                {navigationData.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between">
                        <a
                          href={item.href}
                          className="flex items-center space-x-3 px-4 py-3 rounded-lg text-foreground hover:bg-accent transition-all duration-200 hover:translate-x-1 w-full group"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <IconComponent className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                          <span className="font-medium">{item.title}</span>
                        </a>
                        {item.submenu && (
                          <button
                            onClick={() => setActiveSubmenu(activeSubmenu === index ? null : index)}
                            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-200 hover:scale-110"
                          >
                            <ChevronRight 
                              className={`h-4 w-4 transition-transform duration-200 ${
                                activeSubmenu === index ? 'rotate-90' : ''
                              }`} 
                            />
                          </button>
                        )}
                      </div>
                      
                      {/* Submenu */}
                      {item.submenu && activeSubmenu === index && (
                        <div className="ml-8 mt-2 space-y-1 animate-in slide-in-from-left-2 duration-200 ease-out">
                          {item.submenu.map((subItem, subIndex) => {
                            const SubIconComponent = subItem.icon;
                            return (
                              <a
                                key={subIndex}
                                href={subItem.href}
                                className="flex items-center space-x-3 px-4 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-200 hover:translate-x-1"
                                onClick={() => setIsMobileMenuOpen(false)}
                                style={{ animationDelay: `${subIndex * 50}ms` }}
                              >
                                <SubIconComponent className="h-4 w-4" />
                                <span>{subItem.title}</span>
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border p-4">
              <div className="space-y-4">
                {/* Language Selection */}
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-2">Language</h3>
                  <div className="flex flex-wrap gap-2">
                    {['en', 'ar', 'fr', 'es', 'de', 'it'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          changeLanguage(lang);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          language === lang 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'
                        }`}
                      >
                        {lang.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-2">
                  <a href="/contact" className="w-full">
                    <Button variant="ghost" className="w-full text-muted-foreground hover:text-foreground">
                      {t('nav.contact')}
                    </Button>
                  </a>
                  <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                    {t('nav.joinUp')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-start overflow-hidden">
        {/* Spline 3D Interactive Background */}
        <div 
          id="spline-background" 
          className="absolute inset-0 z-0"
          style={{ width: '100%', height: '100%' }}
        >
          <spline-viewer 
            url="https://prod.spline.design/0mMSNhUsMcEDsegS/scene.splinecode"
            style={{ width: '100%', height: '100%' }}
          ></spline-viewer>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-[-4rem] sm:mt-[-6rem]">
          <div className={`max-w-4xl ${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <h1 className="text-5xl md:text-7xl font-bold text-foreground leading-tight mb-6">
              {t('hero.headline')} <span className="text-primary">{t('hero.brandName')}</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed mb-8 max-w-3xl">
              {t('hero.subheadline')}
            </p>
            <div>
              <Button 
                size="lg" 
                onClick={handleGetStarted}
                disabled={isGetStartedLoading}
                className={`
                  relative overflow-hidden bg-primary text-primary-foreground hover:bg-primary/90 
                  text-lg px-8 py-6 transition-all duration-300 ease-in-out
                  ${buttonClicked ? 'scale-95' : 'hover:scale-105'}
                  ${isGetStartedLoading ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'}
                  group shadow-lg hover:shadow-xl
                `}
              >
                {/* Animated background effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Button content */}
                <div className="relative flex items-center justify-center gap-2">
                  {isGetStartedLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <span>{t('hero.cta')}</span>
                      <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </div>
                
                {/* Ripple effect */}
                {buttonClicked && !isGetStartedLoading && (
                  <div className="absolute inset-0 bg-white/20 animate-ping rounded-md" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-foreground mb-16">
            {t('testimonials.title')}
          </h2>
          
          {/* Top Row */}
          <div className="mb-8">
            <div className="flex animate-marquee-left">
              {testimonials.slice(0, 10).map((testimonial, index) => (
                <Card key={index} className="flex-shrink-0 w-80 mx-4 bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <Avatar>
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {testimonial.name ? testimonial.name.split(' ').map(n => n[0]).join('') : 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-foreground">{testimonial.name}</p>
                        <p className="text-sm text-muted-foreground">{testimonial.company}</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground italic">"{testimonial.quote}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Bottom Row */}
          <div>
            <div className="flex animate-marquee-right">
              {testimonials.slice(10, 20).map((testimonial, index) => (
                <Card key={index} className="flex-shrink-0 w-80 mx-4 bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <Avatar>
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {testimonial.name ? testimonial.name.split(' ').map(n => n[0]).join('') : 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-foreground">{testimonial.name}</p>
                        <p className="text-sm text-muted-foreground">{testimonial.company}</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground italic">"{testimonial.quote}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-16 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              {t('features.title')}
            </h2>
            <p className={`text-xl text-muted-foreground max-w-3xl ${language === 'ar' ? 'ml-auto' : 'mx-auto'}`}>
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-card border-border hover:shadow-lg transition-shadow"
                >
                  <CardContent className="p-8">
                    <div className="mb-4">
                      <IconComponent className="h-12 w-12 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Showcase Section */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-16 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Our Premium Services
            </h2>
            <p className={`text-xl text-muted-foreground max-w-3xl ${language === 'ar' ? 'ml-auto' : 'mx-auto'}`}>
              From AI-powered solutions to custom web development, we deliver comprehensive digital services that drive real business results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Branded Websites",
                description: "Custom-designed websites that convert visitors into customers with modern technology and optimized performance.",
                icon: WebIcon,
                benefits: ["40-60% conversion increase", "Mobile-responsive design", "SEO optimized", "Fast loading speeds"]
              },
              {
                title: "CRM Dashboards",
                description: "Centralized customer data management with automated sales processes and real-time analytics.",
                icon: BarChart3,
                benefits: ["35% productivity boost", "Automated follow-ups", "Real-time insights", "Team collaboration"]
              },
              {
                title: "AI Receptionist",
                description: "24/7 intelligent customer service that handles inquiries, schedules appointments, and provides instant support.",
                icon: Bot,
                benefits: ["60% cost reduction", "24/7 availability", "Multi-language support", "Seamless handoff"]
              },
              {
                title: "Community Management",
                description: "Professional community building that increases engagement, builds loyalty, and drives organic growth.",
                icon: CommunityIcon,
                benefits: ["80% engagement increase", "Brand loyalty building", "User-generated content", "Organic growth"]
              },
              {
                title: "Social Media & Ads",
                description: "Data-driven social strategies and targeted advertising campaigns that increase brand awareness and generate leads.",
                icon: Smartphone,
                benefits: ["200% ROI improvement", "Qualified lead generation", "Brand awareness boost", "Trend management"]
              },
              {
                title: "Automations",
                description: "Custom automation solutions that eliminate manual tasks, improve efficiency, and ensure process reliability.",
                icon: AutomationIcon,
                benefits: ["70% manual work reduction", "Error elimination", "24/7 processes", "Better data collection"]
              }
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
              <Card 
                key={index} 
                className="bg-card border-border hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
              >
                  <CardContent className="p-8">
                    <div className="mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-12 w-12 text-primary" />
                    </div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-center text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full mr-3"></div>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    <a 
                      href="/services" 
                      className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors group/link"
                    >
                      Learn More
                      <svg className="ml-2 h-4 w-4 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </a>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <a 
              href="/services" 
              className="inline-flex items-center px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold text-lg group"
            >
              Explore All Services
              <svg className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Case Studies Preview Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-16 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Success Stories
            </h2>
            <p className={`text-xl text-muted-foreground max-w-3xl ${language === 'ar' ? 'ml-auto' : 'mx-auto'}`}>
              Real results from real businesses. See how we've helped companies transform their operations and achieve remarkable growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                client: "Maroc Telecom",
                industry: "Telecommunications",
                title: "CRM Transformation",
                results: "40% faster response times, 25% higher satisfaction",
                image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
              },
              {
                client: "Attijariwafa Bank",
                industry: "Banking",
                title: "AI Assistant Deployment",
                results: "70% cost reduction, 95% satisfaction rate",
                image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                logo: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
              },
              {
                client: "Royal Air Maroc",
                industry: "Aviation",
                title: "Digital Marketing Success",
                results: "200% engagement increase, 180% traffic growth",
                image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                logo: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
              }
            ].map((caseStudy, index) => (
              <Card 
                key={index} 
                className="bg-card border-border hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={caseStudy.image} 
                    alt={caseStudy.client}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 flex items-center space-x-3">
                    <img 
                      src={caseStudy.logo} 
                      alt={`${caseStudy.client} logo`}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-foreground">{caseStudy.client}</h4>
                      <p className="text-xs text-muted-foreground">{caseStudy.industry}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {caseStudy.title}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {caseStudy.results}
                  </p>
                  <a 
                    href="/case-studies" 
                    className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors group/link"
                  >
                    Read Full Case Study
                    <svg className="ml-2 h-4 w-4 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="/case-studies" 
              className="inline-flex items-center px-8 py-4 border-2 border-primary/20 text-primary rounded-lg hover:bg-primary/5 transition-colors font-semibold text-lg"
            >
              View All Case Studies
              <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Stats & Metrics Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-16 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Proven Results
            </h2>
            <p className={`text-xl text-muted-foreground max-w-3xl ${language === 'ar' ? 'ml-auto' : 'mx-auto'}`}>
              Our track record speaks for itself. Here are the measurable results we've delivered for our clients.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "150+", label: "Happy Clients", description: "Across various industries" },
              { number: "98%", label: "Satisfaction Rate", description: "Average client rating" },
              { number: "200+", label: "Projects Completed", description: "Successful deliveries" },
              { number: "5+", label: "Years Experience", description: "In digital transformation" }
            ].map((stat, index) => (
              <div 
                key={index} 
                className="text-center group"
              >
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-lg font-semibold text-foreground mb-1">
                  {stat.label}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-16 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Industries We Serve
            </h2>
            <p className={`text-xl text-muted-foreground max-w-3xl ${language === 'ar' ? 'ml-auto' : 'mx-auto'}`}>
              We have deep expertise across multiple industries, understanding the unique challenges and opportunities each sector faces.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: "Banking & Finance", icon: Building, clients: "Attijariwafa Bank, Société Générale" },
              { name: "Telecommunications", icon: Phone, clients: "Maroc Telecom, Orange" },
              { name: "Healthcare", icon: Building2, clients: "Medical centers, Clinics" },
              { name: "E-commerce", icon: ShoppingCart, clients: "Marjane, Retail chains" },
              { name: "Aviation", icon: Plane, clients: "Royal Air Maroc, Airlines" },
              { name: "Transportation", icon: Train, clients: "ONCF, Logistics" },
              { name: "Real Estate", icon: Building, clients: "Property developers, Agencies" },
              { name: "Education", icon: GraduationCap, clients: "Universities, Training centers" }
            ].map((industry, index) => {
              const IconComponent = industry.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-card border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
                >
                  <CardContent className="p-6 text-center">
                    <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-8 w-8 text-primary mx-auto" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">
                      {industry.name}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {industry.clients}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-16 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              How We Work
            </h2>
            <p className={`text-xl text-muted-foreground max-w-3xl ${language === 'ar' ? 'ml-auto' : 'mx-auto'}`}>
              Our proven process ensures successful project delivery from initial consultation to ongoing support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Discovery & Strategy",
                description: "We analyze your business needs, goals, and challenges to create a tailored strategy.",
                icon: Search
              },
              {
                step: "02", 
                title: "Design & Planning",
                description: "Our team designs the solution architecture and creates detailed project plans.",
                icon: ClipboardList
              },
              {
                step: "03",
                title: "Development & Testing",
                description: "We build your solution with rigorous testing and quality assurance processes.",
                icon: Settings
              },
              {
                step: "04",
                title: "Launch & Support",
                description: "We launch your solution and provide ongoing support for optimal performance.",
                icon: Rocket
              }
            ].map((process, index) => {
              const IconComponent = process.icon;
              return (
                <div 
                  key={index} 
                  className="relative"
                >
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary text-primary-foreground rounded-full mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-8 w-8" />
                    </div>
                    <div className="text-sm font-bold text-primary mb-2">
                      STEP {process.step}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      {process.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {process.description}
                    </p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-border transform translate-x-4">
                      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-primary rounded-full"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div>
            <h2 className="text-4xl font-bold mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join 150+ successful businesses that have transformed their operations with Envael's innovative solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="inline-flex items-center px-8 py-4 bg-primary-foreground text-primary rounded-lg hover:bg-primary-foreground/90 transition-colors font-semibold text-lg group"
              >
                Start Your Project
                <svg className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a 
                href="/book-consultation" 
                className="inline-flex items-center px-8 py-4 border-2 border-primary-foreground/20 text-primary-foreground rounded-lg hover:bg-primary-foreground/10 transition-colors font-semibold text-lg"
              >
                Book Free Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
};

export default StatsAI;
